// ReceiptCard.tsx
import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Card, Space, Typography, Skeleton, Button } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { API_BASE_URL } from "../constants/config";
import styled from 'styled-components';
import axios from 'axios';
import { getAndSanitize } from "../shared/utils/sanitization";

interface ReceiptCardProps {
  loading: boolean;
  record: {
    type: string;
    receipt?: string;
    withdraw?: {
      receipt?: string;
    };
    operation?: {
      withdraw?: {
        receipt?: string;
      };
      receipt?: string;
    };
  };
  details?: {
    receipt?: string | null;
  } | null;
  onImageClick?: (url: string) => void;
  previewUrl?: string;
}

const ReceiptContainer = styled.div`
  position: relative;
  border-radius: 4px;
  min-height: 200px;
`;

const ReceiptImage = styled.img`
  max-width: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  margin: 0 auto;
`;

export const ReceiptCard: React.FC<ReceiptCardProps> = React.memo(({
  loading,
  record,
  details,
  previewUrl,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const processingRef = useRef(false);
  const blobUrlRef = useRef<string | null>(null);

  const receiptUrl = useMemo(() => {
    if (previewUrl) {
      console.log('[ReceiptCard] Modo preview activo');
      return 'preview-image';
    }

    if (!record && !details) {
      console.warn('[ReceiptCard] No hay record ni details disponibles');
      return null;
    }

    const receiptPath = details?.receipt ||
      record.receipt ||
      record.operation?.receipt ||
      record.withdraw?.receipt ||
      record.operation?.withdraw?.receipt;

    console.log('[ReceiptCard] Receipt path resuelto:', {
      source: details?.receipt ? 'details.receipt' : record.receipt ? 'record.receipt' : record.operation?.receipt ? 'record.operation.receipt' : 'otro',
      rawValue: receiptPath,
    });

    if (!receiptPath) {
      console.warn('[ReceiptCard] receiptPath es null/undefined');
      return null;
    }

    if (receiptPath === 'null' || receiptPath === 'undefined' || receiptPath.trim() === '') {
      console.warn('[ReceiptCard] receiptPath es string vacio o "null":', receiptPath);
      return null;
    }

    let cleanPath = receiptPath;

    if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
      console.log('[ReceiptCard] Receipt es URL externa:', cleanPath.substring(0, 60) + '...');
      return cleanPath;
    }

    if (cleanPath.startsWith('/uploads/')) {
      cleanPath = cleanPath.substring(9);
    } else if (cleanPath.startsWith('uploads/')) {
      cleanPath = cleanPath.substring(8);
    }

    if (!/^[a-zA-Z0-9._\-/]+$/.test(cleanPath)) {
      console.error('[ReceiptCard] Path contiene caracteres invalidos:', cleanPath);
      return null;
    }

    console.log('[ReceiptCard] Path limpio final:', cleanPath);
    return cleanPath;
  }, [previewUrl, details, record]);

  const loadImageWithAuth = useCallback(async () => {
    if (processingRef.current || !receiptUrl) {
      console.log('[ReceiptCard] loadImageWithAuth skipped:', { processing: processingRef.current, receiptUrl });
      return;
    }
    processingRef.current = true;

    console.log('[ReceiptCard] Iniciando carga de imagen...', { receiptUrl, API_BASE_URL });
    setImageLoading(true);
    setImageError(false);

    try {
      if (previewUrl && receiptUrl === 'preview-image') {
        console.log('[ReceiptCard] Usando preview URL');
        setImageSrc(previewUrl);
        setImageLoading(false);
        processingRef.current = false;
        return;
      }

      const token = getAndSanitize("token");
      console.log('[ReceiptCard] Token disponible:', !!token);

      // External URL: fetch as blob to avoid mixed-content and CSP issues
      if (receiptUrl.startsWith('http://') || receiptUrl.startsWith('https://')) {
        console.log('[ReceiptCard] Fetching URL externa como blob...');
        try {
          const response = await axios.get(receiptUrl, { responseType: 'blob', timeout: 15000 });
          console.log('[ReceiptCard] URL externa OK - blob size:', response.data.size, 'type:', response.data.type);
          const blobUrl = URL.createObjectURL(response.data);
          if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = blobUrl;
          setImageSrc(blobUrl);
        } catch (extError: any) {
          console.warn('[ReceiptCard] Fetch externo fallo, usando URL directa. Error:', extError?.message, 'Status:', extError?.response?.status);
          // Fallback: set URL directly (works if same-origin or CORS allows it)
          setImageSrc(receiptUrl);
        }
        return;
      }

      if (!token) {
        console.error('[ReceiptCard] No hay token disponible para solicitud de imagen');
        setImageError(true);
        return;
      }

      const cleanPath = receiptUrl.startsWith('uploads/') ? receiptUrl.substring(8) : receiptUrl;
      const imageUrl = `${API_BASE_URL}/api/panel/uploads/${cleanPath}`;
      console.log('[ReceiptCard] Fetching imagen autenticada:', imageUrl);

      const response = await axios.get(imageUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      console.log('[ReceiptCard] Imagen cargada OK - blob size:', response.data.size, 'type:', response.data.type, 'status:', response.status);

      const blobUrl = URL.createObjectURL(response.data);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = blobUrl;
      setImageSrc(blobUrl);
    } catch (error: any) {
      console.error('[ReceiptCard] ERROR cargando imagen:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        url: error?.config?.url,
        responseHeaders: error?.response?.headers ? Object.fromEntries(Object.entries(error.response.headers).filter(([k]) => ['content-type', 'x-content-type-options', 'content-security-policy', 'cross-origin-resource-policy'].includes(k))) : null,
      });
      setImageError(true);
    } finally {
      setImageLoading(false);
      processingRef.current = false;
    }
  }, [previewUrl, receiptUrl]);

  useEffect(() => {
    loadImageWithAuth();

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [loadImageWithAuth, receiptUrl]);

  const renderReceipt = () => {
    if (!receiptUrl) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Typography.Text type="secondary">No hay comprobante disponible</Typography.Text>
        </div>
      );
    }

    if (receiptUrl !== 'preview-image') {
      const token = getAndSanitize("token");
      if (!token) {
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <Typography.Text type="warning">Se requiere autenticación para ver la imagen</Typography.Text>
          </div>
        );
      }
    }

    if (imageLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Space direction="vertical" align="center">
            <LoadingOutlined style={{ fontSize: 32 }} spin />
            <Typography.Text>Cargando imagen...</Typography.Text>
          </Space>
        </div>
      );
    }

    if (imageError || !imageSrc) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', height: '300px' }}>
          <Typography.Text type="warning" style={{ marginBottom: '16px' }}>Error al cargar la imagen</Typography.Text>
          <Button
            type="primary"
            onClick={loadImageWithAuth}
          >
            Reintentar carga
          </Button>
        </div>
      );
    }

    return (
      <ReceiptContainer ref={containerRef}>
        <ReceiptImage
          ref={imageRef}
          src={imageSrc}
          alt="Comprobante"
          loading="lazy"
          onError={(e) => {
            console.error('[ReceiptCard] <img> onError - no se pudo renderizar la imagen:', {
              src: imageSrc?.substring(0, 80),
              naturalWidth: imageRef.current?.naturalWidth,
              naturalHeight: imageRef.current?.naturalHeight,
              error: e,
            });
            setImageError(true);
          }}
        />
      </ReceiptContainer>
    );
  };

  return (
    <Card
      size="small"
      title={
        <Space>
          {loading && <LoadingOutlined />}
          <Typography.Text strong>
            {record.type === "WITHDRAW" ? "Comprobante de Retiro" : "Comprobante"}
          </Typography.Text>
        </Space>
      }
      className="step-card"
    >
      {loading ? (
        <Skeleton.Image style={{ width: "100%", height: 400 }} active />
      ) : (
        renderReceipt()
      )}
    </Card>
  );
});

ReceiptCard.displayName = "ReceiptCard";

export default ReceiptCard;
